import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useGetCarBrandsQuery } from "../../../app/store/api/carsApi";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import { dialogStyle } from "../../../shared/styles/dialogStyles";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark } from "../../../shared/styles/buttonStyles";
import toast from "react-hot-toast";
import { useUpdateWorkshopMutation } from "../../../app/store/api/workshopApi";

const SECTIONS = {
  onlyWorksOn: { titleKey: "workshopBrands.onlyWorksOn", emptyKey: "workshopBrands.noRestriction", descriptionKey: "workshopBrands.onlyWorksOnDescription" },
  authorized: { titleKey: "workshopBrands.brandAuthorizedService", emptyKey: "workshopBrands.noBrandAuthorization", descriptionKey: "workshopBrands.brandAuthorizedServiceDescription" },
  special: { titleKey: "workshopBrands.specialExpertise", emptyKey: "workshopBrands.noSpecialExpertise", descriptionKey: "workshopBrands.specialExpertiseDescription" },
};

const BrandSection = ({ title, description, emptyText, brands, selectLabel, onSelect }) => (
  <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
    <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Button variant='contained' size='small' onClick={onSelect} startIcon={<AddIcon fontSize='small' />} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" }, whiteSpace: "nowrap" }}>
        {selectLabel}
      </Button>
    </Stack>

    {brands.length > 0 ? (
      <>
        {description && (
          <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3, mb: 2 }}>
            {description}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {brands.map((brand) => (
            <Chip key={brand} label={brand} variant='outlined' sx={{ borderColor: "#e3e6ec" }} />
          ))}
        </Box>
      </>
    ) : (
      <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
        {emptyText}
      </Typography>
    )}
  </Paper>
);

const BrandsSelection = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { data: brands, isLoading: brandsLoading } = useGetCarBrandsQuery();
  const workshopDetails = useSelector(selectWorkshopDetails);
  const [updateWorkshop, { isLoading: saving }] = useUpdateWorkshopMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("onlyWorksOn");
  const [selected, setSelected] = useState(new Set());

  const brandFontStyleHeader = { fontWeight: 500, fontSize: "0.875rem" };
  const fontColor = { color: "#003C59" };

  const savedBrands = useMemo(
    () => ({
      onlyWorksOn: workshopDetails?.brandRepresentations || [],
      authorized: workshopDetails?.exclBrands?.brands || [],
      special: workshopDetails?.specialtyBrands?.brands || [],
    }),
    [workshopDetails],
  );

  const buildPayload = (section, names) => {
    switch (section) {
      case "onlyWorksOn":
        return { brandRepresentations: names };
      case "authorized":
        return { exclBrands: { ...workshopDetails?.exclBrands, brands: names } };
      case "special":
        return { specialtyBrands: { ...workshopDetails?.specialtyBrands, brands: names } };
      default:
        return {};
    }
  };

  const isDirty = useMemo(() => {
    const saved = savedBrands[activeSection];
    return saved.length !== selected.size || saved.some((name) => !selected.has(name));
  }, [savedBrands, activeSection, selected]);

  const handleDialogOpen = (section) => {
    setActiveSection(section);
    setSelected(new Set(savedBrands[section]));
    setDialogOpen(true);
  };

  const handleDialogClose = () => setDialogOpen(false);

  const handleToggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleApply = async () => {
    const payload = buildPayload(activeSection, Array.from(selected));
    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
        setDialogOpen(false);
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );
  };

  return (
    <Box>
      {Object.entries(SECTIONS).map(([key, cfg]) => (
        <BrandSection key={key} title={t(cfg.titleKey)} description={cfg.descriptionKey ? t(cfg.descriptionKey) : null} emptyText={t(cfg.emptyKey)} brands={savedBrands[key]} selectLabel={t("workshopSettings.add")} onSelect={() => handleDialogOpen(key)} />
      ))}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='sm' fullWidth sx={{ ...dialogStyle }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: "bold", ...fontColor }}>
              {t(SECTIONS[activeSection].titleKey)}
            </Typography>
            <Typography variant='body2' sx={{ color: "text.secondary", marginTop: 1, ...fontColor }}>
              {t(SECTIONS[activeSection].descriptionKey)}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} sx={{ padding: 0, marginTop: "-4px" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: isMobile ? "16px" : "36px" }}>
          {brandsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                gap: 1.5,
              }}
            >
              {brands?.map((brand) => {
                const isActive = selected.has(brand.name);
                return (
                  <Alert
                    key={brand.id}
                    icon={isActive ? <CheckCircleOutlineIcon fontSize='small' /> : <RadioButtonUncheckedIcon fontSize='small' />}
                    sx={{
                      backgroundColor: isActive ? "rgb(214, 252, 255)" : "transparent",
                      color: "#003C59",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      m: 0,
                      px: 1.5,
                      py: 0.5,
                      minWidth: 0,
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { backgroundColor: isActive ? "rgb(150, 229, 242)" : "#f5f5f5" },
                      "& .MuiAlert-icon": { color: "#003C59", mr: 1, p: 0, alignSelf: "center" },
                      "& .MuiAlert-message": { p: 0, minWidth: 0, width: "100%", overflow: "hidden" },
                    }}
                    onClick={() => handleToggle(brand.name)}
                  >
                    <Typography variant='body1' noWrap title={brand.name} sx={brandFontStyleHeader}>
                      {brand.name}
                    </Typography>
                  </Alert>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#003C59",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "16px 24px",
            gap: 2,
            zIndex: 1,
          }}
        >
          <Button onClick={handleDialogClose} variant='outlined' sx={{ ...altCloseButtonStyleDark, flex: 1 }}>
            {t("common.close")}
          </Button>
          <Button
            onClick={handleApply}
            variant='contained'
            disabled={saving || !isDirty}
            startIcon={saving ? <CircularProgress size={14} color='inherit' /> : null}
            sx={{ ...altChooseButtonStyleTurquoise, flex: 1, "&.Mui-disabled": { opacity: 0.5, backgroundColor: "#00C9DB", color: "#003C59", cursor: "not-allowed" } }}
          >
            {t("common.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandsSelection;
